import { useEffect, useRef, useState } from "react";
import {
  Store,
  ClipboardCheck,
  Package,
  Rocket,
  Activity,
  Cpu,
  ShoppingBag,
  Truck,
  Utensils,
  CreditCard,
  Home,
  Globe,
  Smartphone,
  LayoutGrid,
  Tag,
  Image as ImageIcon,
  Check,
  MessageCircle,
  BarChart3,
  Boxes,
  Layers,
  Bike,
} from "lucide-react";

/*
 * ServiceWorkflow
 * -----------------------------------------------------------------
 * Full-width, code-native animated workflow panels — one distinct
 * metaphor per service. Semantic HTML with lightweight inline SVG,
 * subtle CSS motion, and full prefers-reduced-motion fallbacks.
 * See /app/frontend/src/index.css → block starting with "ep-wf-*".
 *
 * IntersectionObserver pauses continuous animations when the panel
 * is off-screen for performance (adds/removes .ep-wf-in-view).
 */

/* ------------------------------------------------------------------ */
/* Shared panel shell + IntersectionObserver hook                     */
/* ------------------------------------------------------------------ */

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => setInView(e.isIntersecting));
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Panel({ eyebrow, title, subtitle, testid, children }) {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      className={`ep-wf-section py-14 md:py-20 relative overflow-hidden ${
        inView ? "ep-wf-in-view" : ""
      }`}
      data-testid={testid}
    >
      <div className="ep-glow-tl" aria-hidden="true" />
      <div className="ep-container relative">
        <div className="max-w-3xl mb-8 md:mb-12">
          {eyebrow && (
            <p className="text-xs tracking-[0.3em] text-[#00F22E]/90 font-semibold mb-3">
              {eyebrow}
            </p>
          )}
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-4 text-white/75 text-base md:text-lg leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
        <div className="ep-wf-panel">{children}</div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* 1) PAZARYERİ — horizontal pipeline (5 stages, restart loop)       */
/* ------------------------------------------------------------------ */

function PazaryeriPipeline() {
  const stages = [
    { Icon: ClipboardCheck, title: "Başvuru", desc: "Evrak ve mağaza kaydı" },
    { Icon: Store, title: "Mağaza Kurulumu", desc: "Kanala göre yapılandırma" },
    { Icon: Boxes, title: "Ürün Girişi", desc: "Katalog ve görseller" },
    { Icon: Rocket, title: "Yayına Alma", desc: "Kanallarda canlı" },
    { Icon: Activity, title: "Günlük Operasyon", desc: "Sipariş • kampanya • stok" },
  ];
  return (
    <Panel
      eyebrow="İŞ AKIŞI"
      title={
        <>
          Pazaryeri Süreci{" "}
          <span className="text-[#00F22E]">Nasıl İşliyor?</span>
        </>
      }
      subtitle="Başvurudan günlük operasyona kadar tüm adımlar tek bir hat üzerinde ilerler."
      testid="workflow-pazaryeri"
    >
      <div className="ep-wf-pipe" role="list">
        <span className="ep-wf-pipe-track" aria-hidden="true">
          <span className="ep-wf-pipe-progress" />
        </span>
        {stages.map((s, i) => {
          const Icon = s.Icon;
          return (
            <div
              key={i}
              role="listitem"
              className="ep-wf-pipe-node"
              style={{ "--wf-i": i }}
              data-testid={`workflow-pazaryeri-node-${i}`}
            >
              <div className="ep-wf-pipe-index">0{i + 1}</div>
              <div className="ep-wf-pipe-card">
                <div className="ep-wf-pipe-icon" aria-hidden="true">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="ep-wf-pipe-title">{s.title}</div>
                <div className="ep-wf-pipe-desc">{s.desc}</div>
                <div className="ep-wf-pipe-status">
                  <span className="ep-wf-pipe-status-dot" aria-hidden="true" />
                  <span className="ep-wf-pipe-status-pending">Sırada</span>
                  <span className="ep-wf-pipe-status-active">Aktif</span>
                  <span className="ep-wf-pipe-status-done">
                    <Check className="h-3 w-3" aria-hidden="true" /> Tamam
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* 2) FİYAT VE STOK — bidirectional sync                              */
/* ------------------------------------------------------------------ */

function PriceStockSync() {
  const channels = [
    { label: "Uber Eats Trendyol Go", key: "ch1" },
    { label: "Getir", key: "ch2" },
    { label: "Yemeksepeti", key: "ch3" },
    { label: "Kendi Kanalınız", key: "ch4" },
  ];
  return (
    <Panel
      eyebrow="ANLIK SENKRON"
      title={
        <>
          Fiyat ve Stok{" "}
          <span className="text-[#00F22E]">Tüm Kanallarda Aynı Anda</span>
        </>
      }
      subtitle="Kasa veya stok sisteminizle satış kanalları arasında sürekli, çift yönlü bir veri akışı çalışır."
      testid="workflow-fiyat-stok"
    >
      <div className="ep-wf-sync" data-testid="workflow-fiyat-stok-grid">
        <div className="ep-wf-sync-node ep-wf-sync-source">
          <div className="ep-wf-sync-node-icon" aria-hidden="true">
            <Store className="h-5 w-5" />
          </div>
          <div className="ep-wf-sync-node-title">Kasa / Stok Sistemi</div>
          <div className="ep-wf-sync-node-desc">Kaynak</div>
        </div>

        <div className="ep-wf-sync-wire ep-wf-sync-wire-left" aria-hidden="true">
          <span className="ep-wf-sync-line" />
          <span className="ep-wf-sync-particle ep-wf-sync-p1">Fiyat</span>
          <span className="ep-wf-sync-particle ep-wf-sync-p2 ep-wf-sync-particle-reverse">Stok</span>
          <span className="ep-wf-sync-particle ep-wf-sync-p3">Ürün</span>
        </div>

        <div className="ep-wf-sync-hub">
          <div className="ep-wf-sync-hub-pulse" aria-hidden="true" />
          <div className="ep-wf-sync-hub-icon" aria-hidden="true">
            <Cpu className="h-6 w-6" />
          </div>
          <div className="ep-wf-sync-hub-title">Entegrasyon Merkezi</div>
          <div className="ep-wf-sync-hub-desc">
            <span className="ep-wf-sync-hub-live-dot" aria-hidden="true" />
            Eşleştirme çalışıyor
          </div>
        </div>

        <div className="ep-wf-sync-wire ep-wf-sync-wire-right" aria-hidden="true">
          <span className="ep-wf-sync-line" />
          <span className="ep-wf-sync-particle ep-wf-sync-p4">Fiyat</span>
          <span className="ep-wf-sync-particle ep-wf-sync-p5 ep-wf-sync-particle-reverse">Stok</span>
        </div>

        <div className="ep-wf-sync-channels">
          <div className="ep-wf-sync-channels-title">Satış Kanalları</div>
          <ul className="ep-wf-sync-channels-list">
            {channels.map((c, i) => (
              <li
                key={c.key}
                className="ep-wf-sync-channel"
                style={{ "--wf-i": i }}
                data-testid={`workflow-fiyat-stok-channel-${i}`}
              >
                <span className="ep-wf-sync-channel-dot" aria-hidden="true" />
                <span className="ep-wf-sync-channel-label">{c.label}</span>
                <span className="ep-wf-sync-channel-status" aria-label="Güncel">
                  <Check className="h-3 w-3" aria-hidden="true" />
                  <span className="ep-wf-visually-hidden">Güncellendi</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* 3) KURYE — 5-step delivery status route                            */
/* ------------------------------------------------------------------ */

function CourierRoute() {
  const steps = [
    { Icon: MessageCircle, title: "Sipariş Alındı", note: "Kanal → İşyeri" },
    { Icon: Utensils, title: "Hazırlanıyor", note: "İşyeri hazırlığı" },
    { Icon: Bike, title: "Kurye Atandı", note: "En yakın kurye" },
    { Icon: Truck, title: "Yolda", note: "Rota takibi" },
    { Icon: Home, title: "Teslim Edildi", note: "Müşteride" },
  ];
  return (
    <Panel
      eyebrow="TESLİMAT DURUMU"
      title={
        <>
          Sipariş, Her Aşamada{" "}
          <span className="text-[#00F22E]">Takip Edilebilir.</span>
        </>
      }
      subtitle="Siparişin alındığı andan müşteriye teslim edildiği ana kadar her durum güncellemesi kayıt altına alınır."
      testid="workflow-kurye"
    >
      <ol className="ep-wf-route" data-testid="workflow-kurye-list">
        <span className="ep-wf-route-line" aria-hidden="true">
          <span className="ep-wf-route-line-fill" />
          <span className="ep-wf-route-courier" aria-hidden="true">
            <Bike className="h-4 w-4" />
          </span>
        </span>
        {steps.map((s, i) => {
          const Icon = s.Icon;
          return (
            <li
              key={i}
              className="ep-wf-route-item"
              style={{ "--wf-i": i }}
            >
              <span className="ep-wf-route-node" aria-hidden="true">
                <span className="ep-wf-route-node-inner">
                  <Icon className="h-4 w-4" />
                </span>
              </span>
              <span className="ep-wf-route-check" aria-hidden="true">
                <Check className="h-3 w-3" />
              </span>
              <div className="ep-wf-route-body">
                <div className="ep-wf-route-title">{s.title}</div>
                <div className="ep-wf-route-note">{s.note}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* 4) İŞYERİNE ÖZEL SİPARİŞ — order lifecycle with moving order card  */
/* ------------------------------------------------------------------ */

function OrderLifecycle() {
  const stages = [
    { Icon: Smartphone, title: "Müşteri Siparişi", label: "Alındı" },
    { Icon: Globe, title: "Web / Mobil Sistem", label: "İletildi" },
    { Icon: LayoutGrid, title: "Yönetim Paneli", label: "Onaylandı" },
    { Icon: CreditCard, title: "Ödeme", label: "Alındı" },
    { Icon: Truck, title: "Teslimat", label: "Hazır" },
  ];
  return (
    <Panel
      eyebrow="SİPARİŞ YAŞAM DÖNGÜSÜ"
      title={
        <>
          Sipariş, İşyerinizin Sisteminden{" "}
          <span className="text-[#00F22E]">Müşteriye Ulaşana Kadar</span>
        </>
      }
      subtitle="Müşteri siparişinden teslimat anına kadar tüm adımlar tek bir yönetim panelinden takip edilir."
      testid="workflow-oc"
    >
      <div className="ep-wf-lifecycle" data-testid="workflow-oc-grid">
        <span className="ep-wf-lifecycle-line" aria-hidden="true" />
        <span className="ep-wf-lifecycle-order" aria-hidden="true">
          <span className="ep-wf-lifecycle-order-icon">
            <Package className="h-3.5 w-3.5" />
          </span>
          <span>Sipariş #1048</span>
        </span>
        {stages.map((s, i) => {
          const Icon = s.Icon;
          return (
            <div
              key={i}
              className="ep-wf-lifecycle-stage"
              style={{ "--wf-i": i }}
            >
              <div className="ep-wf-lifecycle-icon" aria-hidden="true">
                <Icon className="h-5 w-5" />
              </div>
              <div className="ep-wf-lifecycle-title">{s.title}</div>
              <div className="ep-wf-lifecycle-status">
                <span className="ep-wf-lifecycle-status-dot" aria-hidden="true" />
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* 5) ÜRÜN / MENÜ — progressive product-card transformation           */
/* ------------------------------------------------------------------ */

function ProductTransformation() {
  const stages = [
    { title: "Ham Ürün Bilgisi", desc: "Ad, adet, kaynak" },
    { title: "Düzenleme", desc: "Açıklama, birim, KDV" },
    { title: "Kategori ve Görsel", desc: "Kategori, marka, foto" },
    { title: "Kontrol", desc: "Barkod, fiyat kontrol" },
    { title: "Yayınlandı", desc: "Kanallara açıldı" },
  ];
  return (
    <Panel
      eyebrow="ÜRÜN DÖNÜŞÜMÜ"
      title={
        <>
          Ham Bilgiden Yayına{" "}
          <span className="text-[#00F22E]">Hazır Ürüne</span>
        </>
      }
      subtitle="Ürünler kanallara açılmadan önce ad, kategori, görsel ve fiyat kontrollerinden geçer."
      testid="workflow-catalog"
    >
      <div className="ep-wf-transform">
        <ol className="ep-wf-transform-steps">
          {stages.map((s, i) => (
            <li key={i} className="ep-wf-transform-step" style={{ "--wf-i": i }}>
              <span className="ep-wf-transform-num">0{i + 1}</span>
              <div className="ep-wf-transform-body">
                <div className="ep-wf-transform-title">{s.title}</div>
                <div className="ep-wf-transform-desc">{s.desc}</div>
              </div>
            </li>
          ))}
        </ol>

        <div className="ep-wf-transform-card" aria-hidden="true">
          <div className="ep-wf-transform-card-image">
            <span className="ep-wf-transform-card-image-icon">
              <ImageIcon className="h-6 w-6" />
            </span>
          </div>
          <div className="ep-wf-transform-card-content">
            <div className="ep-wf-transform-field ep-wf-tf-name">
              <span className="ep-wf-transform-field-label">Ürün adı</span>
              <span className="ep-wf-transform-field-value">Ürün adı ve açıklaması</span>
            </div>
            <div className="ep-wf-transform-field ep-wf-tf-price">
              <span className="ep-wf-transform-field-label">Fiyat</span>
              <span className="ep-wf-transform-field-value">₺ 129,90</span>
            </div>
            <div className="ep-wf-transform-field ep-wf-tf-cat">
              <span className="ep-wf-transform-field-label">Kategori</span>
              <span className="ep-wf-transform-field-value">
                <Tag className="h-3 w-3 inline mr-1" aria-hidden="true" />
                Belirlendi
              </span>
            </div>
            <div className="ep-wf-transform-field ep-wf-tf-status">
              <span className="ep-wf-transform-published">
                <Check className="h-3 w-3" aria-hidden="true" /> Yayında
              </span>
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* 6) DİJİTAL OPERASYON — central hub with async in/out flows         */
/* ------------------------------------------------------------------ */

function OperationsHub() {
  const inputs = [
    { title: "Pazaryerleri", note: "Kanal sipariş akışı" },
    { title: "Kendi Sipariş Sisteminiz", note: "Web ve mobil" },
    { title: "Kurye Ekibi", note: "Teslimat verileri" },
  ];
  const outputs = [
    { Icon: ShoppingBag, title: "Sipariş", tag: "günün 8 saati" },
    { Icon: Boxes, title: "Stok", tag: "kanal başı kontrol" },
    { Icon: Layers, title: "Kampanya", tag: "planlı yayın" },
    { Icon: BarChart3, title: "Rapor", tag: "haftalık özet" },
  ];
  return (
    <Panel
      eyebrow="OPERASYON MERKEZİ"
      title={
        <>
          Tüm Satış Kanalları,{" "}
          <span className="text-[#00F22E]">Tek Bir Operasyon Merkezinde</span>
        </>
      }
      subtitle="Farklı kanallardan gelen sipariş, stok ve kampanya verileri Epersonel operasyon merkezinde işlenir ve ilgili ekibe iletilir."
      testid="workflow-ops-hub"
    >
      <div className="ep-wf-hub" data-testid="workflow-ops-hub-grid">
        <div className="ep-wf-hub-side ep-wf-hub-inputs">
          <div className="ep-wf-hub-side-title">Giriş</div>
          {inputs.map((c, i) => (
            <div
              key={i}
              className="ep-wf-hub-chan ep-wf-hub-chan-in"
              style={{ "--wf-i": i }}
            >
              <span className="ep-wf-hub-chan-dot" aria-hidden="true" />
              <div>
                <div className="ep-wf-hub-chan-title">{c.title}</div>
                <div className="ep-wf-hub-chan-note">{c.note}</div>
              </div>
              <span className="ep-wf-hub-event ep-wf-hub-event-in" aria-hidden="true" />
            </div>
          ))}
        </div>

        <div className="ep-wf-hub-center">
          <div className="ep-wf-hub-center-ring" aria-hidden="true" />
          <div className="ep-wf-hub-center-icon" aria-hidden="true">
            <Cpu className="h-6 w-6" />
          </div>
          <div className="ep-wf-hub-center-title">Epersonel Operasyon Merkezi</div>
          <div className="ep-wf-hub-center-sub">
            <span className="ep-wf-hub-live-dot" aria-hidden="true" />
            Aktif izleme
          </div>
        </div>

        <div className="ep-wf-hub-side ep-wf-hub-outputs">
          <div className="ep-wf-hub-side-title">Çıkış</div>
          {outputs.map((o, i) => {
            const Icon = o.Icon;
            return (
              <div
                key={i}
                className="ep-wf-hub-out"
                style={{ "--wf-i": i }}
              >
                <span className="ep-wf-hub-out-icon" aria-hidden="true">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="ep-wf-hub-out-body">
                  <div className="ep-wf-hub-out-title">{o.title}</div>
                  <div className="ep-wf-hub-out-tag">{o.tag}</div>
                </div>
                <span className="ep-wf-hub-out-pulse" aria-hidden="true" />
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */

export default function ServiceWorkflow({ slug }) {
  switch (slug) {
    case "pazaryeri-kurulum-yonetimi":
      return <PazaryeriPipeline />;
    case "fiyat-ve-stok-entegrasyonu":
      return <PriceStockSync />;
    case "kurye-cozumu":
      return <CourierRoute />;
    case "uye-isyerine-ozel-siparis-sistemi":
      return <OrderLifecycle />;
    case "urun-ve-menu-yonetimi":
      return <ProductTransformation />;
    case "dijital-operasyon-yonetimi":
      return <OperationsHub />;
    default:
      return null;
  }
}
